import { useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  Container,
  Button,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import { BsLine as BsLineIcon } from 'react-icons/bs';
import { FiBell as FiBellIcon } from 'react-icons/fi';
import { AiTwotoneCar as CarIcon } from 'react-icons/ai';
import { useSetAtom, useAtomValue } from 'jotai';
import {
  openLiveChatAtom,
  liveChatSessionVariablesAtom,
} from '@/atoms/livechat';
import { userAtom } from '@/atoms/user';
import { Location as LocationIcon } from '@/assets/icons';
import SwiperBlock from '@/components/pages/buildings/SwiperBlock';
import ProductCard from '@/components/pages/buildings/ProductCard';
import StaticMap from '@/components/shared/GoogleStaticMap';
import { NOT_HEADQUARTER_SERVICE_AREA } from '@/constants';
import Footer from '@/components/shared/Footer';
import { sendAppierEvent } from '@/utils/appier';
import { useSubscriptionType } from '@/hooks/useSubscriptionType';

// FIXME: 先趕快上線，放這邊
const WashingFBIframe = () => {
  return (
    <Box>
      <Text mb='15px' fontSize='16px' fontWeight={500} lineHeight='28px'>
        車主無需在場，透過APP即可預約，專人到點手工洗車輕鬆無負擔！
      </Text>
      <Box position='relative' w='100%' pt='56.25%' overflow='hidden'>
        <iframe
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
          }}
          src='https://www.youtube.com/embed/dCk97SYBUOg?si=OnPwsPHa4y3rC52D'
          title='YouTube video player'
          frameBorder='0'
          allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
          referrerPolicy='strict-origin-when-cross-origin'
          allowFullScreen></iframe>
      </Box>
    </Box>
  );
};

const Main = ({ data }) => {
  const user = useAtomValue(userAtom);
  const { isSubscriptionOnly } = useSubscriptionType(data?.id);
  const setSessionVariables = useSetAtom(liveChatSessionVariablesAtom);
  const openLiveChat = useSetAtom(openLiveChatAtom);

  // 因應台北市停管處標案；USPACE昆陽捷運站旁平面停車場使用抽籤模式。
  // 12/20 改到停管處網站
  // const isButtonsDisabled = data?.id == '357d15cc-2a14-44b8-9aa9-d4933cc6171d';

  const { isHeadquarterSupport } = useSubscriptionType(data?.id);

  useEffect(() => {
    let variables = {};
    if (user) {
      variables = {
        ...variables,
        name: user?.fullname,
        email: user?.email,
        phone: user?.phone,
      };
    }
    // 將目前頁面的網址傳給 LiveChat
    variables = {
      ...variables,
      visited_page: window.location.href,
    };
    setSessionVariables(variables);

    // Appier Event 瀏覽場站資訊頁
    sendAppierEvent('building_subs_viewed', {
      building_name: data.name,
      building_id: data.id,
      building_url: window?.location?.origin + window?.location?.pathname,
      building_city: data.city,
      building_zone: data.zone,
      building_road: data.road,
      building_address: `${data.city}${data.zone}${data.road}`,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data?.subscription_products) {
    return null;
  }

  const isContactHeadquarterSupport =
    !(
      NOT_HEADQUARTER_SERVICE_AREA &&
      [...NOT_HEADQUARTER_SERVICE_AREA].includes(data.city)
    ) || isHeadquarterSupport;

  // 目前僅區分台北和中部
  const lineUrl = isContactHeadquarterSupport
    ? 'https://lin.ee/AcpIrwm'
    : 'https://lin.ee/CxDqSv1';
  const lineDeepLink = isContactHeadquarterSupport
    ? 'line://oaMessage/@hah6443y/?'
    : 'line://oaMessage/@639wesrc/?';

  const hasNoProducts = data?.subscription_products?.length === 0;
  const isProductsUnavailable =
    data?.subscription_products?.length > 0 &&
    data?.subscription_products?.every((p) => p.is_sold);
  const isSoldOut =
    data?.subscription_solution?.fully_booked || isProductsUnavailable;

  // 收合整理一樣條件的月租產品，因為月租產品對應一個車位導致數量龐大，為了呈現簡潔而收斂資料。
  const collapsedProducts = Array.isArray(data.subscription_products)
    ? data.subscription_products
        .sort((a, b) => a.month_price - b.month_price)
        .reduce((accu, product) => {
          const tag = product.tags
            .sort((a, b) => a.slug.localeCompare(b.slug))
            .map((t) => t.slug)
            .join('-');
          const key = `${product.name}-${product.period_type}-${product.reservation}-${product.month_price}-${tag}`;
          if (accu[key]) {
            accu[key].count += 1;
            accu[key].products.push({
              ...product,
            });
          } else {
            accu[key] = {
              ...product,
              fully_booked: data?.subscription_solution?.fully_booked || false,
              products: [
                {
                  ...product,
                },
              ],
              count: 1,
            };
          }
          return accu;
        }, {})
    : [];

  const groupByPeriodType = Object.values(collapsedProducts).reduce(
    (accu, product) => {
      if (accu[product.period_type]) {
        accu[product.period_type].push(product);
      } else {
        accu[product.period_type] = [product];
      }
      return accu;
    },
    {},
  );

  // 保證有位 = 月租，不保證 = 月票
  const groupByIsGuaranteed = Object.values(collapsedProducts).reduce(
    (accu, product) => {
      if (product.reservation == 1) {
        if (accu['rent']) {
          accu['rent'].push(product);
        } else {
          accu['rent'] = [product];
        }
      } else if (product.reservation == 0) {
        if (accu['pass']) {
          accu['pass'].push(product);
        } else {
          accu['pass'] = [product];
        }
      }
      return accu;
    },
    {},
  );

  const slides = data?.spaces
    ?.filter((space) => space.url && space.entry_url)
    .slice(0, 1);

  return (
    <>
      <Flex flex='1' mt='px' direction='column'>
        <Box>
          <Container maxW='1120px' pb='16px'>
            <Box pt={{ base: 4, lg: 10 }} pb={{ base: 4, lg: 10 }}>
              <SwiperBlock slides={slides} />
            </Box>

            <Flex
              minHeight={{
                base: !isSubscriptionOnly ? '550px' : '680px',
                md: !isSubscriptionOnly ? '350px' : '500px',
              }}
              // 啊怎麼minHeight比較高...
              // height={{
              //   base: !isSubscriptionOnly ? '350px' : '380px',
              //   md: !isSubscriptionOnly ? '350px' : '400px',
              // }}
              flexWrap={{ base: 'wrap', md: 'nowrap' }}>
              <Box flex='1 1 auto' order={{ base: 2, md: 1 }}>
                <h1>
                  <Text
                    fontSize={{ base: '2xl', md: '3xl' }}
                    fontWeight='bold'
                    mt={{ base: 4, md: 0 }}
                    mb={4}>
                    {data?.name}
                  </Text>
                </h1>
                {isSubscriptionOnly && <WashingFBIframe />}
                {!isSubscriptionOnly && (
                  <>
                    <Flex
                      color='gray.700'
                      align='center'
                      fontSize={{ base: 'sm', md: 'md' }}>
                      <LocationIcon />
                      <h3>
                        <Text ml='4px'>
                          {data &&
                            ` ${data.city ? data.city : ''}${
                              data.zone ? data.zone : ''
                            } ${data.road ? data.road : ''}`}
                        </Text>
                      </h3>
                    </Flex>
                    <Box mt={4}>
                      <StaticMap lat={data.lat} lng={data.lon} />
                    </Box>
                  </>
                )}
              </Box>

              <Box
                flex={{
                  base: !isSubscriptionOnly ? '1 1 auto' : '1 1 auto',
                  md: !isSubscriptionOnly ? '0 0 350px' : 'auto',
                }}
                ml={{ base: 0, md: 4 }}
                mr={{ base: 0, sm: 4, md: 0 }}
                order={{ base: 1, md: 2 }}>
                <Box
                  style={{
                    // position: 'sticky',
                    top: '120px',
                    left: 0,
                  }}
                  p={6}
                  borderWidth='1px'
                  borderColor='blackAlpha.200'
                  borderRadius='16px'>
                  {!hasNoProducts && (
                    <Flex color='#333333' alignItems='flex-end' mb={6}>
                      <Text fontWeight='bold' fontSize='2xl' color='black'>
                        ${data?.min_price}
                      </Text>
                      {' / 月起'}
                    </Flex>
                  )}
                  {!isProductsUnavailable && (
                    <Button
                      w='100%'
                      as={'a'}
                      href='#subscription-products'
                      leftIcon={<CarIcon color='white' />}
                      my={4}
                      bg='primary.blue'
                      color='white'
                      _hover={{
                        bg: 'primary.darkblue',
                      }}>
                      {isSubscriptionOnly ? '選擇訂閱方案' : '選擇月租方案'}
                    </Button>
                  )}

                  {isSoldOut && (
                    <Box>
                      {!hasNoProducts ? (
                        <>
                          <Text fontWeight='bold' color='black'>
                            很抱歉！<br></br>
                            目前所有車位都在承租中<br></br>
                            如需候位，請點擊下方按鈕。<br></br>
                          </Text>
                          <Button
                            w='100%'
                            as={'a'}
                            href={`/waitlist/${data?.id}`}
                            leftIcon={<FiBellIcon color='white' />}
                            mt={4}
                            bg='primary.blue'
                            color='white'
                            _hover={{
                              bg: 'primary.darkblue',
                            }}>
                            申請候補通知
                          </Button>
                        </>
                      ) : null}

                      <>
                        {isContactHeadquarterSupport ? (
                          <>
                            {/* <Button
                              w='100%'
                              bg='gray.700'
                              color='white'
                              _hover={{
                                bg: 'gray.900',
                              }}
                              my={4}
                              leftIcon={<BsFillChatFillIcon color='white' />}
                              onClick={() => openLiveChat()}>
                              詢問客服
                            </Button> */}
                          </>
                        ) : (
                          <Button
                            w='100%'
                            as={'a'}
                            leftIcon={<BsLineIcon color='white' />}
                            href={lineUrl}
                            bg='#57BE37'
                            color='white'
                            my={4}
                            _hover={{
                              bg: 'green.500',
                            }}>
                            詢問 LINE 客服
                          </Button>
                        )}
                      </>
                    </Box>
                  )}

                  <Tabs fontSize='xs' variant='badge'>
                    ⚡️ 下載{' '}
                    <a href='https://uspace.app.link/download' target='_blank'>
                      USPACE APP{' '}
                    </a>
                    快速進出場站
                  </Tabs>
                </Box>
              </Box>
            </Flex>
          </Container>
        </Box>

        {!hasNoProducts && (
          <Box p={{ base: 0, md: 5 }} backgroundColor='#F0F0F0'>
            <Container maxW='1120px'>
              <Box>
                <Text
                  fontSize={{ base: 'lg', md: '2xl' }}
                  fontWeight='bold'
                  mt={4}
                  id='subscription-products'>
                  {!isSubscriptionOnly ? '停車位月租方案' : '車主訂閱方案'}
                </Text>
                <Flex>
                  <Tabs variant='badge' w='100%'>
                    {/* <TabList>
                      <Tab>全部</Tab>
                      <Tab>月租</Tab>
                      <Tab>月票</Tab>
                      <Tab>全日</Tab>
                      <Tab>日間</Tab>
                      <Tab>夜間</Tab>
                    </TabList> */}
                    <TabPanels>
                      <TabPanel>
                        {Object.values(collapsedProducts).map((product) => (
                          <ProductCard
                            data={product}
                            key={product.id}
                            deepLink={lineDeepLink}
                          />
                        ))}
                      </TabPanel>
                      <TabPanel>
                        {groupByIsGuaranteed &&
                          groupByIsGuaranteed['rent']?.map((product) => (
                            <ProductCard
                              data={product}
                              key={product.id}
                              deepLink={lineDeepLink}
                            />
                          ))}
                      </TabPanel>
                      <TabPanel>
                        {groupByIsGuaranteed &&
                          groupByIsGuaranteed['pass']?.map((product) => (
                            <ProductCard
                              data={product}
                              key={product.id}
                              deepLink={lineDeepLink}
                            />
                          ))}
                      </TabPanel>
                      <TabPanel>
                        {groupByPeriodType &&
                          groupByPeriodType['full_day']?.map((product) => (
                            <ProductCard
                              data={product}
                              key={product.id}
                              deepLink={lineDeepLink}
                            />
                          ))}
                      </TabPanel>
                      <TabPanel>
                        {groupByPeriodType &&
                          groupByPeriodType['day']?.map((product) => (
                            <ProductCard
                              data={product}
                              key={product.id}
                              deepLink={lineDeepLink}
                            />
                          ))}
                      </TabPanel>
                      <TabPanel>
                        {groupByPeriodType &&
                          groupByPeriodType['night']?.map((product) => (
                            <ProductCard
                              data={product}
                              key={product.id}
                              deepLink={lineDeepLink}
                            />
                          ))}
                      </TabPanel>
                    </TabPanels>
                  </Tabs>
                </Flex>
              </Box>
            </Container>
          </Box>
        )}

        {data?.subscription_solution?.web_description ? (
          <Box p={5}>
            <Container maxW='1120px'>
              <Text fontSize='2xl' fontWeight='bold' mb={4}>
                停車場資訊
              </Text>
              <Box lineHeight='25pt'>
                {data?.subscription_solution?.web_description
                  ?.split('\n')
                  .map((line, index) => (
                    <p key={index}>{line}</p>
                  ))}
              </Box>
            </Container>
          </Box>
        ) : null}
      </Flex>
      <Footer />
    </>
  );
};

export default Main;
